import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../shared/api/apiClient';
import { RefreshIcon } from '../../shared/icons';
import { ShopOrderStatus, type ShopOrderDto, type ShopOrderStatusValue } from '../../types';
import '../UserManagement/styles.css';
import './styles.css';

const formatDateTime = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('ru-RU');
};

const formatMoney = (value: number) =>
    value.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const statusLabel = (status: ShopOrderStatusValue): string => {
    switch (status) {
        case ShopOrderStatus.Draft:
            return 'Черновик';
        case ShopOrderStatus.Sent:
            return 'Отправлен';
        case ShopOrderStatus.Closed:
            return 'Закрыт';
        case ShopOrderStatus.Cancelled:
            return 'Отменён';
        default:
            return String(status);
    }
};

const statusBadgeClass = (status: ShopOrderStatusValue): string => {
    switch (status) {
        case ShopOrderStatus.Draft:
            return 'bg-secondary';
        case ShopOrderStatus.Sent:
            return 'bg-primary';
        case ShopOrderStatus.Closed:
            return 'bg-success';
        case ShopOrderStatus.Cancelled:
            return 'bg-danger';
        default:
            return 'bg-secondary';
    }
};

const CancelOrderModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    onSave: (comment: string) => Promise<void>;
}> = ({ isOpen, onClose, onSave }) => {
    const [comment, setComment] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setComment('');
            setLoading(false);
        }
    }, [isOpen]);

    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleSave = async () => {
        if (!comment.trim()) return;
        setLoading(true);
        try {
            await onSave(comment.trim());
            onClose();
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-backdrop-custom" onClick={(event) => event.target === event.currentTarget && onClose()}>
            <div className="modal-dialog-custom animate-in">
                <div className="modal-content-custom">
                    <div className="modal-header-custom">
                        <div>
                            <h3 className="modal-title-custom">Отмена заказа</h3>
                            <p className="modal-subtitle-custom">Комментарий обязателен</p>
                        </div>
                        <button type="button" className="btn-close-custom" onClick={onClose} disabled={loading}>
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>
                    <div className="modal-body-custom">
                        <div className="form-group-custom">
                            <label className="form-label-custom">Комментарий</label>
                            <textarea
                                className="form-input-custom"
                                value={comment}
                                onChange={(event) => setComment(event.target.value)}
                                disabled={loading}
                                maxLength={2000}
                                rows={4}
                            />
                        </div>
                    </div>
                    <div className="modal-footer-custom">
                        <button type="button" className="btn-secondary-custom" onClick={onClose} disabled={loading}>
                            Назад
                        </button>
                        <button
                            type="button"
                            className="btn-primary-custom"
                            onClick={handleSave}
                            disabled={loading || !comment.trim()}
                        >
                            {loading ? <span className="btn-spinner"></span> : <i className="bi bi-x-circle"></i>}
                            Отменить заказ
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const OrderDetail: React.FC<{
    orderId: number;
    onBack: () => void;
    onChanged: () => void;
}> = ({ orderId, onBack, onChanged }) => {
    const [order, setOrder] = useState<ShopOrderDto | null>(null);
    const [comment, setComment] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [acting, setActing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [cancelOpen, setCancelOpen] = useState(false);

    const loadOrder = React.useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const result = await api.get<ShopOrderDto>(`/api/admin/shop-orders/${orderId}`);
            setOrder(result.data);
            setComment(result.data.comment || '');
        } catch (err) {
            setError('Не удалось загрузить заказ');
            console.error('Error loading shop order:', err);
        } finally {
            setLoading(false);
        }
    }, [orderId]);

    useEffect(() => {
        loadOrder();
    }, [loadOrder]);

    const isSent = order?.status === ShopOrderStatus.Sent;

    const handleSaveComment = async () => {
        setSaving(true);
        try {
            const result = await api.put<ShopOrderDto>(`/api/admin/shop-orders/${orderId}/comment`, {
                comment: comment.trim() || null
            });
            setOrder(result.data);
            setComment(result.data.comment || '');
            toast.success('Комментарий сохранён');
            onChanged();
        } finally {
            setSaving(false);
        }
    };

    const handleClose = async () => {
        if (!confirm('Закрыть заказ?')) {
            return;
        }
        setActing(true);
        try {
            const result = await api.post<ShopOrderDto>(`/api/admin/shop-orders/${orderId}/close`);
            setOrder(result.data);
            toast.success('Заказ закрыт');
            onChanged();
        } finally {
            setActing(false);
        }
    };

    const handleCancel = async (cancelComment: string) => {
        setActing(true);
        try {
            const result = await api.post<ShopOrderDto>(`/api/admin/shop-orders/${orderId}/cancel`, {
                comment: cancelComment
            });
            setOrder(result.data);
            setComment(result.data.comment || '');
            toast.success('Заказ отменён');
            onChanged();
        } finally {
            setActing(false);
        }
    };

    if (error) {
        return (
            <div className="alert alert-danger">
                <h5 className="alert-heading">Ошибка</h5>
                <p>{error}</p>
                <button className="btn btn-primary" onClick={loadOrder}>Повторить загрузку</button>
                <button className="btn btn-secondary ml-2" onClick={onBack}>К списку</button>
            </div>
        );
    }

    if (loading && !order) {
        return (
            <div className="text-center py-4">
                <span className="btn-spinner"></span>
            </div>
        );
    }

    if (!order) {
        return null;
    }

    return (
        <div className="user-management container-fluid">
            <div className="filter-controls-compact">
                <div className="filter-controls-row">
                    <div className="shop-detail-heading">
                        <button className="btn-compact" onClick={onBack} title="К списку">
                            <i className="bi bi-arrow-left"></i>
                            <span>К списку</span>
                        </button>
                        <h3>Заказ #{order.number ?? order.id}</h3>
                        <span className={`badge ${statusBadgeClass(order.status)}`}>{statusLabel(order.status)}</span>
                    </div>
                    <div className="filter-controls-right">
                        <button className="btn-compact refresh" onClick={loadOrder} disabled={loading} title="Обновить">
                            {loading ? <span className="btn-spinner"></span> : <RefreshIcon />}
                        </button>
                    </div>
                </div>
            </div>

            <div className="shop-panel">
                <div className="shop-order-meta">
                    <div><span className="text-muted">Компания</span><div>{order.companyName}</div></div>
                    <div><span className="text-muted">Автор</span><div>{order.authorUserName}</div></div>
                    <div><span className="text-muted">Менеджер по закупкам</span><div>{order.managerUserName || '-'}</div></div>
                    <div><span className="text-muted">Отправлен</span><div>{formatDateTime(order.sentAt)}</div></div>
                </div>
            </div>

            <div className="shop-panel">
                <h4>Строки</h4>
                <div className="user-management-table-container">
                    <table className="user-management-table">
                        <thead>
                            <tr>
                                <th>Название</th>
                                <th>Артикул</th>
                                <th>Цена</th>
                                <th>Кол-во</th>
                                <th>Комментарий</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(order.lines || []).map(line => (
                                <tr key={line.id}>
                                    <td>{line.name}</td>
                                    <td>{line.sku || '-'}</td>
                                    <td>{formatMoney(line.unitPrice)}</td>
                                    <td>{line.quantity}</td>
                                    <td>{line.lineComment || '-'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {(order.lines || []).length === 0 && (
                    <div className="text-center py-4">
                        <p className="text-muted">Строки отсутствуют</p>
                    </div>
                )}
            </div>

            <div className="shop-panel">
                <h4>Комментарий заказа</h4>
                <textarea
                    className="form-input-custom"
                    value={comment}
                    onChange={(event) => setComment(event.target.value)}
                    maxLength={2000}
                    rows={4}
                    disabled={saving}
                />
                <div className="shop-panel-actions mt-3">
                    <button type="button" className="btn-primary-custom" onClick={handleSaveComment} disabled={saving}>
                        {saving ? <span className="btn-spinner"></span> : <i className="bi bi-check-lg"></i>}
                        Сохранить
                    </button>
                </div>
            </div>

            <div className="shop-panel-actions shop-order-actions">
                <button
                    type="button"
                    className="btn-primary-custom"
                    onClick={handleClose}
                    disabled={!isSent || acting}
                    title={isSent ? 'Закрыть заказ' : 'Доступно только для отправленных заказов'}
                >
                    Закрыть
                </button>
                <button
                    type="button"
                    className="btn-secondary-custom"
                    onClick={() => setCancelOpen(true)}
                    disabled={!isSent || acting}
                    title={isSent ? 'Отменить заказ' : 'Доступно только для отправленных заказов'}
                >
                    Отменить
                </button>
            </div>

            <CancelOrderModal
                isOpen={cancelOpen}
                onClose={() => setCancelOpen(false)}
                onSave={handleCancel}
            />
        </div>
    );
};

const ShopOrderManagement: React.FC = () => {
    const [orders, setOrders] = useState<ShopOrderDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);

    const loadOrders = async () => {
        try {
            setLoading(true);
            setError(null);
            const result = await api.get<{ orders: ShopOrderDto[] }>('/api/admin/shop-orders');
            setOrders(result.data.orders || []);
        } catch (err) {
            setError('Произошла ошибка при загрузке заказов');
            console.error('Error loading shop orders:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadOrders();
    }, []);

    const filteredOrders = useMemo(() => {
        if (statusFilter === '') {
            return orders;
        }
        const status = Number(statusFilter);
        return orders.filter(order => order.status === status);
    }, [orders, statusFilter]);

    if (selectedOrderId != null) {
        return (
            <OrderDetail
                orderId={selectedOrderId}
                onBack={() => setSelectedOrderId(null)}
                onChanged={loadOrders}
            />
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger">
                <h5 className="alert-heading">Ошибка</h5>
                <p>{error}</p>
                <button className="btn btn-primary" onClick={loadOrders}>Повторить загрузку</button>
            </div>
        );
    }

    return (
        <div className="user-management container-fluid">
            <div className="filter-controls-compact">
                <div className="filter-controls-row">
                    <h3>Заказы</h3>
                    <div className="filter-controls-right">
                        <div className="role-filter-compact">
                            <select
                                className="form-select-compact"
                                value={statusFilter}
                                onChange={(event) => setStatusFilter(event.target.value)}
                            >
                                <option value="">Все статусы</option>
                                <option value={ShopOrderStatus.Draft}>Черновик</option>
                                <option value={ShopOrderStatus.Sent}>Отправлен</option>
                                <option value={ShopOrderStatus.Closed}>Закрыт</option>
                                <option value={ShopOrderStatus.Cancelled}>Отменён</option>
                            </select>
                        </div>
                        <button className="btn-compact refresh" onClick={loadOrders} disabled={loading} title="Обновить">
                            {loading ? <span className="btn-spinner"></span> : <RefreshIcon />}
                        </button>
                    </div>
                </div>
            </div>

            <div className="user-management-table-container">
                <table className="user-management-table">
                    <thead>
                        <tr>
                            <th>Компания</th>
                            <th>Автор</th>
                            <th>Менеджер по закупкам</th>
                            <th>Статус</th>
                            <th>Дата отправки</th>
                            <th className="actions-column"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredOrders.map(order => (
                            <tr
                                key={order.id}
                                className="shop-clickable-row"
                                onClick={() => setSelectedOrderId(order.id)}
                            >
                                <td><strong>{order.companyName}</strong></td>
                                <td>{order.authorUserName}</td>
                                <td>{order.managerUserName || '-'}</td>
                                <td>
                                    <span className={`badge ${statusBadgeClass(order.status)}`}>
                                        {statusLabel(order.status)}
                                    </span>
                                </td>
                                <td>{formatDateTime(order.sentAt)}</td>
                                <td className="actions-column">
                                    <div className="btn-group btn-group-sm">
                                        <button
                                            className="btn btn-small btn-edit"
                                            title="Открыть"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                setSelectedOrderId(order.id);
                                            }}
                                        >
                                            <i className="bi bi-box-arrow-in-right"></i>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {filteredOrders.length === 0 && (
                <div className="text-center py-4">
                    <p className="text-muted">Заказы не найдены</p>
                </div>
            )}

            <div className="mt-3 text-muted">
                Показано {filteredOrders.length} из {orders.length} заказов
            </div>
        </div>
    );
};

export default ShopOrderManagement;
